import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router';
import ProtectedRoute from '../components/ProtectedRoute';
import EarlyAccessProvider from '../components/common/EarlyAccessProvider';
import PublicLayout from '../layouts/PublicLayout';
import HomePage from '../pages/HomePage';
import WelcomePage from '../pages/WelcomePage';
import LenderMarketplaceLanding from '../pages/LenderMarketplaceLanding';
import RealtorLandingPage from '../pages/RealtorLandingPage';
import AboutPage from '../pages/AboutPage';
import CounterPage from '../pages/CounterPage';
import DashboardPage from '../pages/DashboardPage';
import SettingsPage from '../pages/SettingsPage';
import NotFoundPage from '../pages/NotFoundPage';
import LoanApplicationPage from '../pages/LoanApplicationPage';
import ReadinessPage from '../pages/ReadinessPage';
import AccountCreationPage from '../pages/AccountCreationPage';
import ErrorBoundary from '../components/ErrorBoundary';

// Underwriter Portal (hidden)
import UnderwriterLoginPage from '../pages/underwriter/UnderwriterLoginPage';
import UnderwriterLayout from '../pages/underwriter/UnderwriterLayout';
import UnderwriterDashboard from '../pages/underwriter/UnderwriterDashboard';
import UnderwriterPipeline from '../pages/underwriter/UnderwriterPipeline';
import UnderwriterQueue from '../pages/underwriter/UnderwriterQueue';
import UnderwriterApplicationView from '../pages/underwriter/UnderwriterApplicationView';

// Lender Portal (hidden)
import LenderLoginPage from '../pages/lender/LenderLoginPage';
import LenderLayout from '../pages/lender/LenderLayout';
import LenderMarketplace from '../pages/lender/LenderMarketplace';
import LenderBids from '../pages/lender/LenderBids';
import LenderPortfolio from '../pages/lender/LenderPortfolio';

// Admin Portal (hidden - exclusive access)
import AdminLoginPage from '../pages/admin/AdminLoginPage';
import AdminDashboard from '../pages/admin/AdminDashboard';
import AdminOverview from '../pages/admin/AdminOverview';
import AdminApplications from '../pages/admin/AdminApplications';
import AdminUnderwriters from '../pages/admin/AdminUnderwriters';
import AdminLenders from '../pages/admin/AdminLenders';
import AdminReferrals from '../pages/admin/AdminReferrals';
import AdminPrograms from '../pages/admin/AdminPrograms';
import AdminConditions from '../pages/admin/AdminConditions';
import AdminPricing from '../pages/admin/AdminPricing';

// Borrower Portal
import BorrowerLoginPage from '../pages/borrower/BorrowerLoginPage';
import BorrowerPortalDashboard from '../pages/borrower/BorrowerDashboard';
import BorrowerApplication from '../pages/borrower/BorrowerApplication';
import BorrowerDocuments from '../pages/borrower/BorrowerDocuments';
import BorrowerStatus from '../pages/borrower/BorrowerStatus';

// Referral Partner Portal
import ReferralPartnerLoginPage from '../pages/referral/ReferralPartnerLoginPage';
import ReferralPartnerDashboard from '../pages/referral/ReferralPartnerDashboard';
import ReferralsList from '../pages/referral/ReferralsList';
import EarningsPage from '../pages/referral/EarningsPage';

// Wrap every route in EarlyAccessProvider so any page can call
// useEarlyAccess() to either open the ComingSoonModal (pre-launch) or
// navigate into the live application.
const withEarlyAccess = (element) => (
  <EarlyAccessProvider>{element}</EarlyAccessProvider>
);

// Define routes
const router = createBrowserRouter([
  // WelcomePage standalone (no header)
  {
    path: '/',
    element: withEarlyAccess(<WelcomePage />),
    errorElement: <ErrorBoundary />,
  },
  // Apply page standalone (no header)
  {
    path: '/apply',
    element: withEarlyAccess(<LoanApplicationPage />),
    errorElement: <ErrorBoundary />,
  },
  // Owen readiness experience standalone (own header + compliance footer)
  {
    path: '/readiness',
    element: <ReadinessPage />,
    errorElement: <ErrorBoundary />,
  },
  // Public lender marketplace landing
  {
    path: '/lenders',
    element: withEarlyAccess(<LenderMarketplaceLanding />),
    errorElement: <ErrorBoundary />,
  },
  // Public realtor referral landing (education only, no signup)
  {
    path: '/realtors',
    element: withEarlyAccess(<RealtorLandingPage />),
    errorElement: <ErrorBoundary />,
  },
  // Account creation standalone (no header)
  {
    path: '/create-account',
    element: <AccountCreationPage />,
    errorElement: <ErrorBoundary />,
  },
  // Borrower Portal Routes
  {
    path: '/borrower/dashboard',
    element: withEarlyAccess(<BorrowerPortalDashboard />),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/borrower/application/:id',
    element: withEarlyAccess(<BorrowerApplication />),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/borrower/application',
    element: withEarlyAccess(<BorrowerApplication />),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/borrower/documents',
    element: withEarlyAccess(<BorrowerDocuments />),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/borrower/status',
    element: withEarlyAccess(<BorrowerStatus />),
    errorElement: <ErrorBoundary />,
  },
  // Pages with header
  {
    path: '/pages',
    element: <PublicLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: 'home',
        element: <HomePage />,
      },
      {
        path: 'about',
        element: <AboutPage />,
      },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'counter',
        element: (
          <ProtectedRoute>
            <CounterPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
  // Login redirects — all borrower login goes through /borrower/login
  {
    path: '/login',
    element: <Navigate to="/borrower/login" replace />,
  },
  {
    path: '/my-applications',
    element: <Navigate to="/borrower/login" replace />,
  },
  {
    path: '/borrower/login',
    element: withEarlyAccess(<BorrowerLoginPage />),
    errorElement: <ErrorBoundary />,
  },
  // Hidden Underwriter Portal Routes
  {
    path: '/uw/login',
    element: <UnderwriterLoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/uw',
    element: <UnderwriterLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: 'dashboard',
        element: <UnderwriterDashboard />,
      },
      {
        path: 'pipeline',
        element: <UnderwriterPipeline />,
      },
      {
        path: 'queue',
        element: <UnderwriterQueue />,
      },
      {
        path: 'application/:id',
        element: <UnderwriterApplicationView />,
      },
    ],
  },
  // Hidden Lender Portal Routes
  {
    path: '/lender/login',
    element: <LenderLoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/lender',
    element: <LenderLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: 'marketplace',
        element: <LenderMarketplace />,
      },
      {
        path: 'bids',
        element: <LenderBids />,
      },
      {
        path: 'portfolio',
        element: <LenderPortfolio />,
      },
    ],
  },
  // Hidden Admin Portal Routes (exclusive access: mark@loanknow.com)
  {
    path: '/admin/login',
    element: <AdminLoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/admin',
    element: <AdminDashboard />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: 'dashboard',
        element: <AdminOverview />,
      },
      {
        path: 'applications',
        element: <AdminApplications />,
      },
      {
        path: 'underwriters',
        element: <AdminUnderwriters />,
      },
      {
        path: 'lenders',
        element: <AdminLenders />,
      },
      {
        path: 'referrals',
        element: <AdminReferrals />,
      },
      {
        path: 'programs',
        element: <AdminPrograms />,
      },
      {
        path: 'conditions',
        element: <AdminConditions />,
      },
      {
        path: 'pricing',
        element: <AdminPricing />,
      },
    ],
  },
  // Referral Partner Portal Routes
  {
    path: '/partner/login',
    element: <ReferralPartnerLoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/referral',
    element: <Outlet />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: 'dashboard',
        element: <ReferralPartnerDashboard />,
      },
      {
        path: 'referrals',
        element: <ReferralsList />,
      },
      {
        path: 'earnings',
        element: <EarningsPage />,
      },
    ],
  },
]);

// Router component
export const Router = () => {
  return <RouterProvider router={router} />;
};

export default Router;
